import { useEffect, useMemo, useState } from 'react'
import { ChevronRight, Search, X } from 'lucide-react'
import { apiGet, apiPatch, apiPost } from '../lib/api'
import { useToast } from '../context/ToastContext'
import { Badge, Button, FormField, Input, Label, Modal, Select } from './ui'
import { useResourceGroupsEnabled } from '../hooks/useResourceGroupsEnabled'

export interface ModalResource {
  id: string
  name: string
  type: string
}

export interface ModalPolicy {
  id: string
  resource_id?: string
  tag_group_id?: string
  effect?: string
  allowed_subjects: string[]
  allowed_groups: string[]
}

interface TagGroupOption {
  id: string
  name: string
  member_count?: number
}

interface GroupItem {
  claim_value: string
  label: string
}

export interface CreatePolicyModalProps {
  tenantId: string
  resources: ModalResource[]
  open: boolean
  onClose: () => void
  /** Non-null puts the modal in edit mode; the resource select is locked. */
  editingPolicy?: ModalPolicy | null
  /** Pre-fills the resource select on create, but leaves it editable — used
   * by the Policies page when creating from an already-selected resource
   * facet, where switching resources mid-form is still a reasonable thing
   * to do. */
  initialResourceId?: string
  /** Pre-fills AND locks the resource select, same as edit mode — used by
   * the Resources page's "Add Policy" action on a specific row, where the
   * whole point of the entry point is "a policy for *this* resource". */
  lockResourceId?: string
  /** Called after a successful create/update with the affected resource_id,
   * so the caller can do a targeted refetch (e.g. a policy-count badge)
   * instead of reloading everything. */
  onSaved: (resourceId: string) => void
}

// Shared between AdminPoliciesPage (create/edit from the Policies browser)
// and AdminResourcesPage (the "Add Policy" action on a zero-policy resource
// row) — a policy is nothing but a resource↔group/subject binding, so both
// entry points open the exact same form rather than each inventing their own.
export function CreatePolicyModal({
  tenantId,
  resources,
  open,
  onClose,
  editingPolicy = null,
  initialResourceId,
  lockResourceId,
  onSaved,
}: CreatePolicyModalProps) {
  const { toast } = useToast()
  const { enabled: resourceGroupsEnabled } = useResourceGroupsEnabled(tenantId)

  const [targetType, setTargetType] = useState<'resource' | 'tag_group'>('resource')
  const [formTagGroupId, setFormTagGroupId] = useState('')
  const [effect, setEffect] = useState<'allow' | 'deny'>('allow')
  const [tagGroups, setTagGroups] = useState<TagGroupOption[]>([])
  const [tagGroupQuery, setTagGroupQuery] = useState('')
  const [memberPreview, setMemberPreview] = useState<number | null>(null)

  const [availableGroups, setAvailableGroups] = useState<GroupItem[]>([])
  const [groupsSource, setGroupsSource] = useState('')
  const [formResourceId, setFormResourceId] = useState('')
  const [selectedGroups, setSelectedGroups] = useState<string[]>([])
  const [allowedSubjects, setAllowedSubjects] = useState('')
  const [showAdvanced, setShowAdvanced] = useState(false)
  const [saving, setSaving] = useState(false)
  const [formError, setFormError] = useState<string | null>(null)

  const [resourceQuery, setResourceQuery] = useState('')
  const [resourceTypeFilter, setResourceTypeFilter] = useState('')
  const [resourcePickerOpen, setResourcePickerOpen] = useState(false)
  const [groupQuery, setGroupQuery] = useState('')

  // Independent fetch — the modal owns its own group list rather than
  // depending on whichever page opened it having already fetched one.
  useEffect(() => {
    if (!open || !tenantId) return
    apiGet<{
      source: string
      items: { claim_value: string; display_name: string; idp_id: string | null; idp_name: string }[]
    }>(`/api/v1/tenants/${tenantId}/groups`)
      .then((d) => {
        setGroupsSource(d?.source ?? '')
        const items = d?.items ?? []
        setAvailableGroups(
          items.map((g) => ({
            claim_value: g.claim_value,
            label: g.idp_id ? `${g.claim_value} (${g.idp_name})` : `${g.claim_value} — ${g.idp_name}`,
          })),
        )
      })
      .catch(() => setAvailableGroups([]))
  }, [open, tenantId])

  useEffect(() => {
    if (!open || !tenantId || !resourceGroupsEnabled || targetType !== 'tag_group') {
      return
    }
    const t = setTimeout(() => {
      const qs = new URLSearchParams({ limit: '25', offset: '0' })
      const q = tagGroupQuery.trim()
      if (q) qs.set('q', q)
      apiGet<{ items: TagGroupOption[] }>(`/api/v1/tenants/${tenantId}/tag-groups?${qs}`)
        .then((d) => setTagGroups(d.items ?? []))
        .catch(() => setTagGroups([]))
    }, 300)
    return () => clearTimeout(t)
  }, [open, tenantId, resourceGroupsEnabled, targetType, tagGroupQuery])

  useEffect(() => {
    if (!open) return
    if (editingPolicy) {
      if (editingPolicy.tag_group_id) {
        setTargetType('tag_group')
        setFormTagGroupId(editingPolicy.tag_group_id)
        setFormResourceId('')
      } else {
        setTargetType('resource')
        setFormResourceId(editingPolicy.resource_id ?? '')
        setFormTagGroupId('')
      }
      setEffect((editingPolicy.effect === 'deny' ? 'deny' : 'allow'))
      setSelectedGroups(editingPolicy.allowed_groups ?? [])
      setAllowedSubjects((editingPolicy.allowed_subjects ?? []).join(', '))
      setShowAdvanced((editingPolicy.allowed_subjects?.length ?? 0) > 0)
    } else {
      setTargetType(lockResourceId || initialResourceId ? 'resource' : 'resource')
      setFormResourceId(lockResourceId ?? initialResourceId ?? '')
      setFormTagGroupId('')
      setEffect('allow')
      setSelectedGroups([])
      setAllowedSubjects('')
      setShowAdvanced(false)
    }
    setResourceQuery('')
    setResourceTypeFilter('')
    setResourcePickerOpen(false)
    setGroupQuery('')
    setTagGroupQuery('')
    setMemberPreview(null)
    setFormError(null)
  }, [open, editingPolicy, initialResourceId, lockResourceId])

  useEffect(() => {
    if (!open || targetType !== 'tag_group' || !formTagGroupId || !tenantId) {
      setMemberPreview(null)
      return
    }
    apiGet<{ match_count: number }>(`/api/v1/tenants/${tenantId}/tag-groups/${formTagGroupId}/preview`)
      .then((d) => setMemberPreview(d.match_count))
      .catch(() => setMemberPreview(null))
  }, [open, targetType, formTagGroupId, tenantId])

  const toggleGroup = (claim: string) => {
    setSelectedGroups((prev) => (prev.includes(claim) ? prev.filter((g) => g !== claim) : [...prev, claim]))
  }

  const resourceLocked = !!editingPolicy || !!lockResourceId
  const selectedResource = resources.find((r) => r.id === formResourceId)
  const selectedTagGroup = tagGroups.find((g) => g.id === formTagGroupId)

  const resourceTypes = useMemo(
    () => [...new Set(resources.map((r) => r.type))].sort(),
    [resources],
  )

  const filteredResources = useMemo(() => {
    const q = resourceQuery.trim().toLowerCase()
    return resources.filter((r) => {
      if (resourceTypeFilter && r.type !== resourceTypeFilter) return false
      if (!q) return true
      return r.name.toLowerCase().includes(q) || r.type.toLowerCase().includes(q)
    })
  }, [resources, resourceQuery, resourceTypeFilter])

  const selectedGroupItems = availableGroups.filter((g) => selectedGroups.includes(g.claim_value))
  const filteredAvailableGroups = useMemo(() => {
    const q = groupQuery.trim().toLowerCase()
    return availableGroups.filter((g) => !selectedGroups.includes(g.claim_value) && (!q || g.label.toLowerCase().includes(q)))
  }, [availableGroups, groupQuery, selectedGroups])

  const subjectList = allowedSubjects.split(',').map((x) => x.trim()).filter(Boolean)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (targetType === 'resource' && !formResourceId) {
      setFormError('Resource is required.')
      return
    }
    if (targetType === 'tag_group' && !formTagGroupId) {
      setFormError('Resource group is required.')
      return
    }
    if (selectedGroups.length === 0 && allowedSubjects.trim() === '') {
      setFormError('Grant at least one group or a specific subject.')
      return
    }
    setSaving(true)
    setFormError(null)
    try {
      const body = {
        allowed_groups: selectedGroups,
        allowed_subjects: subjectList,
        effect,
      }
      if (editingPolicy) {
        const patchUrl = editingPolicy.tag_group_id
          ? `/api/v1/tenants/${tenantId}/policies/${editingPolicy.id}`
          : `/api/v1/tenants/${tenantId}/resources/${editingPolicy.resource_id}/policies/${editingPolicy.id}`
        await apiPatch(patchUrl, body)
        toast({ title: 'Policy updated', variant: 'success' })
        onSaved(editingPolicy.resource_id ?? '')
      } else if (targetType === 'tag_group') {
        await apiPost(`/api/v1/tenants/${tenantId}/policies`, {
          ...body,
          tag_group_id: formTagGroupId,
          tag_group_name: selectedTagGroup?.name,
        })
        toast({ title: 'Policy created', variant: 'success' })
        onSaved('')
      } else {
        await apiPost(`/api/v1/tenants/${tenantId}/resources/${formResourceId}/policies`, {
          ...body,
          resource_name: selectedResource?.name,
        })
        toast({ title: 'Policy created', variant: 'success' })
        onSaved(formResourceId)
      }
      onClose()
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : 'Save failed'
      setFormError(msg)
      toast({ title: 'Save failed', description: msg, variant: 'danger' })
    } finally {
      setSaving(false)
    }
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={editingPolicy ? 'Edit Policy' : 'Create Policy'}
      description={selectedResource?.name}
      className="max-w-xl"
      footer={
        <>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button type="submit" form="policy-form" disabled={saving}>
            {saving ? 'Saving…' : editingPolicy ? 'Save Changes' : 'Create Policy'}
          </Button>
        </>
      }
    >
      <form id="policy-form" onSubmit={handleSubmit} className="space-y-4">
        {formError && (
          <div className="rounded-lg border border-danger/30 bg-danger-muted px-3 py-2 text-sm text-danger-foreground">
            {formError}
          </div>
        )}

        {resourceGroupsEnabled && !editingPolicy && !lockResourceId && (
          <FormField label="Target type" htmlFor="target-type">
            <Select
              id="target-type"
              value={targetType}
              onChange={(e) => {
                const v = e.target.value as 'resource' | 'tag_group'
                setTargetType(v)
                if (v === 'resource') setFormTagGroupId('')
                else setFormResourceId('')
              }}
            >
              <option value="resource">Resource</option>
              <option value="tag_group">Resource group</option>
            </Select>
          </FormField>
        )}

        {resourceGroupsEnabled && (
          <FormField label="Effect" htmlFor="policy-effect">
            <Select id="policy-effect" value={effect} onChange={(e) => setEffect(e.target.value as 'allow' | 'deny')}>
              <option value="allow">Allow</option>
              <option value="deny">Deny</option>
            </Select>
          </FormField>
        )}

        {targetType === 'tag_group' ? (
          <div>
            <Label className="mb-1 block">Resource group</Label>
            <Input
              className="mb-2"
              placeholder="Search resource groups…"
              value={tagGroupQuery}
              onChange={(e) => setTagGroupQuery(e.target.value)}
            />
            <div className="max-h-40 overflow-y-auto rounded-lg border border-border p-1">
              {tagGroups.length === 0 ? (
                <p className="px-2 py-3 text-center text-xs text-muted-foreground">
                  {tagGroupQuery.trim() ? 'No resource groups match.' : 'Type to search resource groups.'}
                </p>
              ) : (
                tagGroups.map((g) => (
                <button
                  key={g.id}
                  type="button"
                  onClick={() => setFormTagGroupId(g.id)}
                  className={`flex w-full justify-between rounded-md px-2.5 py-1.5 text-left text-sm hover:bg-surface-muted ${
                    g.id === formTagGroupId ? 'bg-primary/10 text-primary' : ''
                  }`}
                >
                  <span>{g.name}</span>
                  <span className="text-xs text-muted">{g.member_count ?? 0} members</span>
                </button>
              ))
              )}
            </div>
            {memberPreview !== null && formTagGroupId && (
              <p className="mt-2 text-xs text-muted">Currently matches {memberPreview} resources</p>
            )}
          </div>
        ) : (
        <div>
          <Label className="mb-1 block">Resource</Label>
          {resourceLocked ? (
            <div className="rounded-lg border border-border bg-surface-muted px-3 py-2 text-sm">
              {selectedResource ? (
                <>
                  <span className="font-medium text-foreground">{selectedResource.name}</span>{' '}
                  <span className="text-xs text-muted-foreground">{selectedResource.type}</span>
                </>
              ) : (
                <span className="text-muted-foreground">{formResourceId}</span>
              )}
            </div>
          ) : !resourcePickerOpen && selectedResource ? (
            <button
              type="button"
              onClick={() => setResourcePickerOpen(true)}
              className="flex w-full items-center justify-between rounded-lg border border-border px-3 py-2 text-left text-sm hover:bg-surface-muted"
            >
              <span>
                <span className="font-medium text-foreground">{selectedResource.name}</span>{' '}
                <span className="text-xs text-muted-foreground">{selectedResource.type}</span>
              </span>
              <span className="text-xs text-primary">Change</span>
            </button>
          ) : (
            <div className="rounded-lg border border-border">
              <div className="flex items-center gap-2 border-b border-border p-2">
                <div className="relative flex-1">
                  <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted" />
                  <Input
                    autoFocus
                    className="h-8 pl-8 text-sm"
                    placeholder="Search resources…"
                    value={resourceQuery}
                    onChange={(e) => setResourceQuery(e.target.value)}
                  />
                </div>
                {resourceTypes.length > 1 && (
                  <select
                    className="h-8 rounded-md border border-border bg-surface px-2 text-xs text-foreground"
                    value={resourceTypeFilter}
                    onChange={(e) => setResourceTypeFilter(e.target.value)}
                  >
                    <option value="">All types</option>
                    {resourceTypes.map((t) => (
                      <option key={t} value={t}>{t}</option>
                    ))}
                  </select>
                )}
              </div>
              <div className="max-h-52 overflow-y-auto p-1">
                {filteredResources.length === 0 ? (
                  <p className="px-2 py-3 text-center text-xs text-muted-foreground">No resources match.</p>
                ) : (
                  filteredResources.map((r) => (
                    <button
                      key={r.id}
                      type="button"
                      onClick={() => {
                        setFormResourceId(r.id)
                        setResourcePickerOpen(false)
                      }}
                      className={`flex w-full items-center justify-between gap-2 rounded-md px-2.5 py-1.5 text-left text-sm hover:bg-surface-muted ${
                        r.id === formResourceId ? 'bg-primary/10 text-primary' : 'text-foreground'
                      }`}
                    >
                      <span className="truncate">{r.name}</span>
                      <span className="flex-shrink-0 text-xs text-muted-foreground">{r.type}</span>
                    </button>
                  ))
                )}
              </div>
            </div>
          )}
        </div>
        )}

        <div>
          <Label className="mb-1 block">Access Groups</Label>
          <p className="mb-2 text-xs text-muted">
            {groupsSource === 'scim'
              ? 'Groups from the SCIM directory cache, labeled by IdP.'
              : 'Groups from Authentik live lookup, used until an IdP is SCIM-onboarded.'}
          </p>

          {selectedGroupItems.length > 0 && (
            <div className="mb-2 flex flex-wrap gap-1.5">
              {selectedGroupItems.map((g) => (
                <Badge key={g.claim_value} variant="primary" className="gap-1 pr-1">
                  {g.label}
                  <button
                    type="button"
                    onClick={() => toggleGroup(g.claim_value)}
                    className="rounded-full p-0.5 hover:bg-primary/20"
                    aria-label={`Remove ${g.label}`}
                  >
                    <X className="h-3 w-3" />
                  </button>
                </Badge>
              ))}
            </div>
          )}

          {availableGroups.length > 0 ? (
            <div className="rounded-lg border border-border">
              <div className="border-b border-border p-2">
                <div className="relative">
                  <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted" />
                  <Input
                    className="h-8 pl-8 text-sm"
                    placeholder="Search groups…"
                    value={groupQuery}
                    onChange={(e) => setGroupQuery(e.target.value)}
                  />
                </div>
              </div>
              <div className="max-h-44 overflow-y-auto p-1">
                {filteredAvailableGroups.length === 0 ? (
                  <p className="px-2 py-3 text-center text-xs text-muted-foreground">
                    {groupQuery ? 'No groups match.' : 'All groups selected.'}
                  </p>
                ) : (
                  filteredAvailableGroups.map((g) => (
                    <button
                      key={g.claim_value}
                      type="button"
                      onClick={() => toggleGroup(g.claim_value)}
                      className="flex w-full items-center rounded-md px-2.5 py-1.5 text-left text-sm text-foreground hover:bg-surface-muted"
                    >
                      {g.label}
                    </button>
                  ))
                )}
              </div>
            </div>
          ) : (
            <Input
              value={selectedGroups.join(', ')}
              onChange={(e) => setSelectedGroups(e.target.value.split(',').map((x) => x.trim()).filter(Boolean))}
              placeholder="hr-users, finance-users"
            />
          )}
        </div>

        <div>
          <button
            type="button"
            onClick={() => setShowAdvanced((v) => !v)}
            className="flex items-center gap-1 text-xs text-muted hover:text-foreground"
          >
            <ChevronRight className={`h-3.5 w-3.5 transition-transform ${showAdvanced ? 'rotate-90' : ''}`} />
            Advanced: allow specific users
          </button>
          {showAdvanced && (
            <FormField label="Allowed Subjects" htmlFor="policy-subjects" hint="Comma-separated email addresses" className="mt-3">
              <Input
                id="policy-subjects"
                value={allowedSubjects}
                onChange={(e) => setAllowedSubjects(e.target.value)}
                placeholder="alice@corp.com, bob@corp.com"
              />
            </FormField>
          )}
        </div>

        {(selectedResource || selectedTagGroup || selectedGroupItems.length > 0 || subjectList.length > 0) && (
          <div className="rounded-lg border border-border bg-surface-muted px-3.5 py-3 text-xs">
            <p className="mb-1.5 font-semibold uppercase tracking-wide text-muted-foreground">Summary</p>
            <p className="text-foreground">
              <span className="font-medium">
                {targetType === 'tag_group'
                  ? selectedTagGroup?.name ?? '— no resource group selected —'
                  : selectedResource?.name ?? '— no resource selected —'}
              </span>
              {' → '}
              <span className="font-medium">
                {selectedGroupItems.length === 0 && subjectList.length === 0
                  ? 'nobody yet'
                  : [...selectedGroupItems.map((g) => g.label), ...subjectList].join(', ')}
              </span>
              {' → '}
              <span className="font-medium">{effect === 'deny' ? 'Deny' : 'Allow'}</span>
            </p>
            {targetType === 'tag_group' && memberPreview !== null && (
              <p className="mt-1 text-muted">Currently matches {memberPreview} resources</p>
            )}
          </div>
        )}
      </form>
    </Modal>
  )
}
